import { Button } from "@/components/ui/button";
import { FC, useEffect } from "react";
import { VideoTexture } from "three";

type Props = {
  hostVideoUrl: string;
  welcomeImageUrl: string;
  setHasAcceptedPermissions: (value: boolean) => void;
  setHasPlayedHostVideo: (value: boolean) => void;
  setLoadedVideoTexture: (texture: THREE.VideoTexture) => void;
  setLoadedVideo: (video: HTMLVideoElement) => void;
};

/**
 * WelcomePage component.
 *
 * This component renders a welcome page with a background image and a button to enter.
 * It requests device orientation permissions and loads a video when the button is clicked.
 *
 * Props:
 * - hostVideo: The source URL of the host video.
 * - setHasAcceptedPermissions: Function to update the state indicating if permissions were accepted.
 * - setHasPlayedHostVideo: Function to update the state indicating if the host video has been played.
 * - setLoadedVideo: Function to update the state with the loaded video element.
 * - setLoadedVideoTexture: Function to update the state with the loaded video texture.
 *
 * @param {Object} props - The component props.
 * @param {string} props.hostVideoUrl - The source URL of the host video.
 * @param {string} props.welcomeImageUrl - The source URL of the welcome image background.
 * @param {Function} props.setHasAcceptedPermissions - Function to update the state indicating if permissions were accepted.
 * @param {Function} props.setHasPlayedHostVideo - Function to update the state indicating if the host video has been played.
 * @param {Function} props.setLoadedVideo - Function to update the state with the loaded video element.
 * @param {Function} props.setLoadedVideoTexture - Function to update the state with the loaded video texture.
 *
 * @returns {JSX.Element} The rendered WelcomePage component.
 */
const WelcomePage: FC<Props> = ({
  hostVideoUrl,
  welcomeImageUrl,
  setHasAcceptedPermissions,
  setHasPlayedHostVideo,
  setLoadedVideo,
  setLoadedVideoTexture,
}: Props) => {
  /**
   * Requests permission for device orientation events if required.
   *
   * This function checks if the `DeviceOrientationEvent.requestPermission` method exists.
   * If it does, it requests permission and sets the state to indicate whether the permission
   * was granted. If the method does not exist, it assumes no permissions are needed and sets
   * the state accordingly.
   *
   * @returns {void}
   */
  const requestDevicePermission = () => {
    if (
      typeof (DeviceOrientationEvent as any).requestPermission === "function"
    ) {
      // Request permission for device orientation events
      (DeviceOrientationEvent as any)
        .requestPermission()
        .then((state: string) => {
          if (state === "granted") {
            setHasAcceptedPermissions(true);
          }
        })
        .catch(console.error);
    } else {
      // No permissions needed
      setHasAcceptedPermissions(true);
    }
  };

  /**
   * Loads a video element, sets its properties, and creates a VideoTexture from it.
   *
   * @returns {HTMLVideoElement} The created video element.
   *
   * The function performs the following steps:
   * 1. Creates a video element and sets its source, looping, muted, autoplay, and playsInline properties.
   * 2. Adds an event listener to set a state when the video ends.
   * 3. Loads the video.
   * 4. Creates a VideoTexture from the video element and updates its properties.
   * 5. Sets the video and texture in the state.
   * 6. Requests device permissions.
   */
  const loadVideo = () => {
    if (hostVideoUrl) {
      // Create a video element
      const videoElement = document.createElement("video");
      videoElement.src = hostVideoUrl;
      videoElement.crossOrigin = "anonymous"; // Ensure cross-origin is handled if needed
      videoElement.loop = false;
      videoElement.muted = false;
      videoElement.autoplay = false;
      videoElement.playsInline = true;

      videoElement.onended = () => {
        setHasPlayedHostVideo(true);
      };

      videoElement.load();

      const texture = new VideoTexture(videoElement);
      texture.needsUpdate = true;
      texture.colorSpace = "srgb";

      setLoadedVideo(videoElement);
      setLoadedVideoTexture(texture);
    }

    requestDevicePermission();

    return;
  };

  return (
    <div
      style={{
        position: "absolute",
        width: "100vw",
        height: "100dvh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f4f5f8",
        zIndex: "999",
      }}
    >
      <div
        style={{
          backgroundImage: 'url("' + welcomeImageUrl + '")',
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          padding: "30px",
          // borderRadius: "10px",
          marginInline: "40px",
          position: "fixed",
          bottom: "0",
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            position: "absolute",
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "40px",
            fontWeight: "bold",
            fontFamily: "Noto Sans",
            // top: "61.5%",
            bottom: "2vh",
          }}
        >
          <img
            style={{
              width: "30px",
            }}
            src="/images/NBSoundUp.gif"
          />
          <h2 className="text-lg text-destructive">Turn Your Volume Up</h2>
        </div>
        <Button
          className="text-primary-foreground m-3 text-3xl bottom-16 absolute"
          onClick={loadVideo}
        >
          Tap to Enter
        </Button>
      </div>
    </div>
  );
};

export default WelcomePage;
